import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import classes from './EntityPage.module.css';

/** Кольори з мокапа: аварія червона, просадка бурштинова, аномалія після деплою — синя. */
const INCIDENT_COLORS: Record<string, string> = {
  down: 'rose',
  index_drop: 'amber',
  deploy_anomaly: 'blue',
};

const INCIDENT_LABELS: Record<string, string> = {
  down: '5xx',
  index_drop: 'INDEX',
  deploy_anomaly: 'DEPLOY',
};

/**
 * «12 хв», «3 год», «5 дн» — так мокап підписує вік події.
 *
 * Точна дата тут гірша за відносний час: оператор дивиться на стрічку, щоб зрозуміти, що
 * сталося щойно, а не щоб звірити хронологію.
 */
function agoLabel(openedAt: Date, now: Date): string {
  const minutes = Math.max(0, Math.round((now.getTime() - openedAt.getTime()) / 60_000));
  if (minutes < 60) return `${String(minutes)} хв`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${String(hours)} год`;
  return `${String(Math.round(hours / 24))} дн`;
}

/**
 * The mockup's dashboard (line 128): five KPI tiles, each a door into the list it counts.
 *
 * It used to show four diagnostic tiles — role, geo scope, readable zones, request id — and a
 * paragraph explaining that the real ones would arrive later. The screen-inventory gate scored
 * the application's front page at **zero** interactive elements against a target of thirteen,
 * and `dashboard.overview` had been implemented, tested and never called from the interface.
 *
 * Every number here is read, not composed on the client: the tile that says how many sites are
 * live and the registry it opens answer the same query on the server, so they cannot disagree.
 */

interface Kpi {
  label: string;
  value: string;
  note: string;
  /** Where the number lives in full. A tile that cannot be opened is a poster, not a control. */
  to: '/sites' | '/monitoring' | '/content-generator';
  testId: string;
  tone?: 'danger';
}

const nf = new Intl.NumberFormat('uk-UA');

function compact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 10_000) return `${(value / 1000).toFixed(1)}k`;
  return nf.format(value);
}

function signed(percent: number): string {
  return `${percent > 0 ? '+' : ''}${String(percent)}%`;
}

export function DashboardPage(): React.JSX.Element {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const overview = useQuery(trpc.dashboard.overview.queryOptions({ days: 28 }));

  /*
   * Таблиця KPI тягнеться лише коли її розгорнули: це запит по всіх сайтах із зверненням до
   * аналітики, і платити за нього на кожному відкритті головної заради панелі, яку дивляться
   * раз на день, не варто.
   */
  const [showKpi, setShowKpi] = useState(false);
  const kpi = useQuery({
    ...trpc.dashboard.kpi.queryOptions({ days: 28, limit: 20 }),
    enabled: showKpi,
  });

  if (overview.isPending) {
    return (
      <Box className={classes.panel} data-testid="dashboard-loading">
        <Loader size="sm" />
      </Box>
    );
  }

  if (overview.error) {
    // A failed query must never read as "everything is at zero" — which is precisely what a
    // dashboard of counters would look like.
    return (
      <Box className={classes.panel} data-testid="dashboard-error">
        <Text className={classes.panelTitle}>Не вдалося завантажити дашборд</Text>
        <Text className={classes.panelBody}>{publicMessageOf(overview.error)}</Text>
      </Box>
    );
  }

  const {
    counts,
    traffic,
    health,
    production,
    pipeline,
    backup,
    databaseBackupsDisabled,
    analyticsDegraded,
  } = overview.data;
  /*
   * Один момент часу на весь рендер стрічки: `new Date()` у кожному рядку дав би дві події,
   * що сталися одночасно, різний вік на різних рядках.
   */
  const now = new Date();

  const kpis: Kpi[] = [
    {
      label: 'Задеплоєно',
      value: compact(counts.sitesLive),
      note: `з ${nf.format(counts.sitesTotal)} у реєстрі`,
      to: '/sites',
      testId: 'kpi-deployed',
    },
    {
      label: 'Доменів у індексі',
      value: compact(counts.sitesIndexed),
      note:
        counts.sitesLive === 0
          ? 'живих сайтів немає'
          : `${String(Math.round((counts.sitesIndexed / counts.sitesLive) * 100))}% з ${nf.format(counts.sitesLive)} живих`,
      to: '/monitoring',
      testId: 'kpi-indexed',
    },
    {
      label: 'Кліки / 28д',
      value: analyticsDegraded ? '—' : compact(traffic.current.clicks),
      // Says so rather than showing a zero: ClickHouse being down is not "no traffic".
      note: analyticsDegraded ? 'аналітика недоступна' : signed(traffic.deltaClicksPct),
      to: '/monitoring',
      testId: 'kpi-clicks',
    },
    {
      label: 'Абуз-кейси',
      value: nf.format(health.openAbuseCases),
      note: `${nf.format(health.criticalAbuseCases)} критичних`,
      to: '/monitoring',
      testId: 'kpi-abuse',
      ...(health.criticalAbuseCases > 0 ? { tone: 'danger' as const } : {}),
    },
    {
      label: 'Черга генерації',
      value: nf.format(counts.generationQueued),
      note: `${nf.format(production.guestPostsReady)} гест-постів готово`,
      to: '/content-generator',
      testId: 'kpi-queue',
    },
  ];

  return (
    <Stack gap="lg">
      <Group gap="md" align="stretch" wrap="wrap" data-testid="dashboard-kpis">
        {kpis.map((kpi) => (
          <UnstyledButton
            key={kpi.testId}
            className={classes.tile}
            data-testid={kpi.testId}
            onClick={() => {
              void navigate({ to: kpi.to });
            }}
          >
            <Text className={classes.tileLabel}>{kpi.label}</Text>
            <Text className={kpi.tone === 'danger' ? classes.tileValueDanger : classes.tileValue}>
              {kpi.value}
            </Text>
            <Text className={classes.dim}>{kpi.note}</Text>
          </UnstyledButton>
        ))}
      </Group>

      {/*
       * Графік Search Console за 28 днів — те, заради чого дашборд відкривають щоранку.
       * Рисується стовпчиками з тих самих даних, що й плитка: без нього «−12%» не каже, чи
       * це один провал, чи рівне сповзання, а це різні реакції.
       *
       * Недоступна аналітика показується словами, а не рівним нулем: ClickHouse, що лежить,
       * і трафік, якого немає, — різні речі, і сплутати їх коштує дня пошуків.
       */}
      <Box className={classes.panel} data-testid="dashboard-traffic">
        <Group justify="space-between" mb={8}>
          <Text className={classes.panelTitle}>Search Console · 28 днів</Text>
          <Text className={classes.dim}>
            {analyticsDegraded
              ? 'аналітика недоступна'
              : `${nf.format(traffic.current.impressions)} показів · CTR ${(traffic.current.ctr * 100).toFixed(2)}% · позиція ${traffic.current.position.toFixed(1)}`}
          </Text>
        </Group>
        {analyticsDegraded ? (
          <Text className={classes.warn} data-testid="traffic-degraded">
            Сховище аналітики не відповідає — це не «нуль кліків», а відсутня відповідь.
          </Text>
        ) : traffic.series.length === 0 ? (
          <Text className={classes.dim} data-testid="traffic-empty">
            Даних ще немає: жодне property не привʼязано до сайтів.
          </Text>
        ) : (
          <Group gap={2} align="flex-end" style={{ height: 96 }} data-testid="traffic-series">
            {(() => {
              /*
               * Два кольори на стовпчик, як у мокапі: покази тлом, кліки — суцільним поверх.
               *
               * Одні кліки не відрізняють «нас перестали показувати» від «нас показують і не
               * клікають» — а це протилежні діагнози: перше лікують технікою, друге текстом.
               * Шкала спільна на весь ряд і рахується один раз: `Math.max` усередині `map`
               * перебирав увесь ряд на кожен день, тобто 28×28 разів на кожен рендер.
               */
              const peakClicks = Math.max(...traffic.series.map((d) => d.clicks), 1);
              const peakImpressions = Math.max(...traffic.series.map((d) => d.impressions), 1);
              return traffic.series.map((day) => (
                <Box
                  key={day.date}
                  title={`${day.date}: ${nf.format(day.clicks)} кліків із ${nf.format(day.impressions)} показів`}
                  data-testid={`traffic-day-${day.date}`}
                  style={{
                    width: 10,
                    height: `${String(Math.max(2, Math.round((day.impressions / peakImpressions) * 96)))}px`,
                    background: 'var(--line3)',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-end',
                  }}
                >
                  <Box
                    data-testid={`traffic-clicks-${day.date}`}
                    style={{
                      width: '100%',
                      height: `${String(Math.max(2, Math.round((day.clicks / peakClicks) * 96)))}px`,
                      background: 'var(--g-txt)',
                      borderRadius: 2,
                    }}
                  />
                </Box>
              ));
            })()}
          </Group>
        )}
      </Box>

      <Box className={classes.panel} data-testid="dashboard-deploy-state">
        <Text className={classes.panelTitle}>Стан деплою</Text>
        <Stack gap={6} mt={8}>
          <DeployRow
            label="Live · контент згенеровано"
            value={counts.sitesLive}
            testId="deploy-live"
          />
          <DeployRow
            label="У збірці, чекає виливки"
            value={counts.sitesBuilding}
            testId="deploy-building"
          />
          <DeployRow
            label="Заморожено після абузу"
            value={counts.sitesWithAbuse}
            testId="deploy-abused"
          />
          <DeployRow
            label="Відкриті інциденти"
            value={health.openIncidents}
            testId="deploy-incidents"
          />
        </Stack>
      </Box>

      <Box className={classes.panel} data-testid="dashboard-backup-status">
        <Group justify="space-between" align="center">
          <Text className={classes.panelTitle}>Резервні копії баз даних</Text>
          <Badge
            color={
              databaseBackupsDisabled
                ? 'orange'
                : backup?.status === 'succeeded' && !backup.stale
                  ? 'green'
                  : 'red'
            }
            data-testid="backup-status-badge"
          >
            {databaseBackupsDisabled
              ? 'ВИМКНЕНО'
              : backup === null
                ? 'НЕМАЄ'
                : backup.status === 'succeeded' && !backup.stale
                  ? 'АКТУАЛЬНИЙ'
                  : backup.stale
                    ? 'ПРОСТРОЧЕНИЙ'
                    : 'ПОМИЛКА'}
          </Badge>
        </Group>
        <Text
          className={
            !databaseBackupsDisabled && backup?.status === 'succeeded' && !backup.stale
              ? classes.dim
              : classes.warn
          }
          mt={6}
        >
          {databaseBackupsDisabled
            ? 'Тимчасовий deployment override вимкнув PostgreSQL і ClickHouse бекапи; відновлення після втрати даних недоступне.'
            : backup === null
              ? 'Жодного реального бекапу ще не зафіксовано.'
              : `PostgreSQL: ${backup.postgresStatus} · ClickHouse: ${backup.clickhouseStatus} · ${
                  backup.finishedAt === null
                    ? 'прогін не завершено'
                    : backup.finishedAt.toLocaleString('uk-UA')
                }`}
        </Text>
      </Box>

      {/*
       * Найгірші сайти — стрічка інцидентів мокапа. `worstSites` рахувався й не показувався
       * ніде: дашборд повідомляв «7 інцидентів» і не казав, де саме, тож наступним кроком
       * оператора був обхід реєстру руками.
       */}
      {/*
       * Стрічка інцидентів — подія з часом, а не хронічний стан.
       *
       * Панель «Де саме болить» нижче показує найгірші сайти за SEO-скором, і її помилково
       * рахували стрічкою інцидентів. Різниця видно тоді, коли вона важлива: домен зі скором
       * 38 висить у списку тижнями, а 503 стався пʼять хвилин тому й може зникнути сам.
       */}
      <Box className={classes.panel} data-testid="dashboard-incidents">
        <Text className={classes.panelTitle} mb={8}>
          Інциденти · останні
        </Text>
        {health.recentIncidents.length === 0 ? (
          <Text className={classes.dim} data-testid="incidents-empty">
            Відкритих інцидентів немає
          </Text>
        ) : (
          <Stack gap={4}>
            {health.recentIncidents.map((incident) => (
              <UnstyledButton
                key={incident.id}
                data-testid={`incident-row-${incident.domain}`}
                onClick={() => {
                  void navigate({ to: '/sites/$siteId', params: { siteId: incident.siteId } });
                }}
              >
                <Group gap={8} wrap="nowrap">
                  <Badge size="xs" color={INCIDENT_COLORS[incident.kind] ?? 'graphite'}>
                    {INCIDENT_LABELS[incident.kind] ?? incident.kind}
                  </Badge>
                  <Text className={classes.mono} size="sm">
                    {incident.domain}
                  </Text>
                  <Text className={classes.dim} size="sm">
                    {incident.note}
                  </Text>
                  <Text
                    className={classes.dim}
                    size="sm"
                    data-testid={`incident-ago-${incident.domain}`}
                  >
                    {agoLabel(incident.openedAt, now)}
                  </Text>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        )}
      </Box>

      {/*
       * Лійка робіт із мокапа: скільки сайтів мережі дійшло до кожного етапу. Кожен етап —
       * умова на колонці, тож числа не можуть розійтися з тим, що насправді в базі.
       */}
      <Box className={classes.panel} data-testid="dashboard-pipeline">
        <Text className={classes.panelTitle} mb={8}>
          Пайплайн робіт
        </Text>
        {pipeline.length === 0 ? (
          <Text className={classes.dim} data-testid="pipeline-empty">
            У мережі ще немає сайтів.
          </Text>
        ) : (
          <Stack gap={6}>
            {pipeline.map((stage) => (
              <Group key={stage.key} gap={10} data-testid={`pipeline-${stage.key}`}>
                <Text size="sm" style={{ width: 180 }}>
                  {stage.label}
                </Text>
                <Progress
                  value={stage.total === 0 ? 0 : (stage.done / stage.total) * 100}
                  size="sm"
                  color="mint"
                  style={{ flex: 1 }}
                />
                <Text className={classes.mono} size="sm" style={{ width: 90 }}>
                  {stage.done} / {stage.total}
                </Text>
              </Group>
            ))}
          </Stack>
        )}
      </Box>

      <Box className={classes.panel} data-testid="dashboard-worst">
        <Text className={classes.panelTitle} mb={8}>
          Де саме болить
        </Text>
        {health.worstSites.length === 0 ? (
          <Text className={classes.dim} data-testid="worst-empty">
            Відкритих інцидентів немає
          </Text>
        ) : (
          <Stack gap={4}>
            {health.worstSites.map((site) => (
              <UnstyledButton
                key={site.siteId}
                data-testid={`worst-${site.domain}`}
                onClick={() => {
                  void navigate({ to: '/sites/$siteId', params: { siteId: site.siteId } });
                }}
              >
                <Group gap={8}>
                  <Text className={classes.mono}>{site.total}</Text>
                  <Text size="sm">{site.domain}</Text>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        )}
      </Box>

      {/*
       * KPI по сайтах — `dashboard.kpi` існував без викликача. Це та таблиця, яка відповідає
       * на «який сайт тягне, а який лежить»: кліки поруч зі SEO-скором і кількістю сторінок.
       */}
      <Box className={classes.panel} data-testid="dashboard-kpi-table">
        <Group justify="space-between" mb={8}>
          <Text className={classes.panelTitle}>Сайти за кліками</Text>
          <Button
            size="compact-xs"
            variant="subtle"
            data-testid="kpi-toggle"
            onClick={() => {
              setShowKpi((previous) => !previous);
            }}
          >
            {showKpi ? 'Згорнути' : 'Показати'}
          </Button>
        </Group>
        {!showKpi ? null : kpi.isPending ? (
          <Text className={classes.dim}>Рахуємо…</Text>
        ) : (kpi.data ?? []).length === 0 ? (
          <Text className={classes.dim} data-testid="kpi-empty">
            Немає сайтів із даними Search Console
          </Text>
        ) : (
          <Stack gap={2}>
            {(kpi.data ?? []).map((row) => (
              <Group key={row.siteId} gap={10} data-testid={`kpi-row-${row.domain}`}>
                <Text className={classes.mono} style={{ width: 70 }}>
                  {nf.format(row.clicks)}
                </Text>
                <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                  {row.domain}
                </Text>
                {/* Скор без даних — прочерк, а не нуль: нуль читався б як «усе погано». */}
                <Text className={classes.dim} style={{ width: 90 }}>
                  скор {row.seoScore ?? '—'}
                </Text>
                <Text className={classes.dim} style={{ width: 90 }}>
                  {nf.format(row.pages)} стор.
                </Text>
              </Group>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

function DeployRow({
  label,
  value,
  testId,
}: {
  label: string;
  value: number;
  testId: string;
}): React.JSX.Element {
  return (
    <Group gap={8} data-testid={testId}>
      <Text className={classes.dim}>{label}</Text>
      <Box style={{ flex: 1, borderBottom: '1px dotted var(--line3)' }} />
      <Text className={classes.mono}>{nf.format(value)}</Text>
    </Group>
  );
}
